import { dataService } from '../../data';

import { DataObject, ObjectOptions, RelatedObject } from "../base";
import { CreateObject } from "../item";
import { Offer } from './offer';

/****************************/
/* SCHEMA DEFINITION        */
/****************************/

import { OfferType } from '../types';

const _schema = {
    "name": "OFFERPERIOD",
    "fields": [
        { name: "status", type: "string", default: "AC" },
        { name: "offer", type: "objid" },
        { name: "weekdays", type: "string" },
        { name: "ini", type: "string" },
        { name: "end", type: "string" },
        { name: "timezone", type: "string" }
    ],
    relate: [
        { direction: ">", target: "OFFER", by: "offer", name: "offer", reason: "offer", child: false, class: OfferType }
    ]
} as const;

const _OfferPeriodClass = CreateObject(_schema);
if (!_OfferPeriodClass) {
    throw new Error("Failed to create BaseClass for '" + _schema.name + "'");
}

/****************************/
/* CLASS DECLARATION        */
/****************************/

export class OfferPeriod extends _OfferPeriodClass { 
    constructor(objid: string | null, data: dataService, objoptions: ObjectOptions | null = null){
        super(_schema, objid, data, objoptions || {});
    }

    Copy(store: Array<DataObject> = []): OfferPeriod {
        return this._Copy(store) as OfferPeriod;
    }

    protected override DoChildLinked(relation: string | null, related: RelatedObject | null): void {
        if (this.CopyOf || !this.Commited){
            return;
        }

        if (relation === 'offer' && related instanceof Offer){
            if (related === this.offer){
                related.AddPeriod(this);
            }
        }
    }

    /****************************/
    /* CLASS MEMBERS            */
    /****************************/
    
    get status(): string | null {
        return this['__base__status'];
    }

    set status(value: string){
        if (this.status == 'DE'){
            return;     // cannot modify deleted items
        }

        if (this.status == value){
            return;
        }

        this['__base__status'] = value;
        if (this.offer){
            if ((this.status == 'DE') && !this.Commited){
                this.offer.DelPeriod(this);
            }
            else {
                this.offer.DoRefresh(this.table);
            }    
        }
    }    
    
    /****************************/
    /* CUSTOM METHODS           */
    /****************************/

    get IsValid(): boolean {
        return (!!this.status && (this.status != 'DE'));
    }

    /**
     * Whether this period row is active at `at`: status AC and weekday + `[ini, end)`
     * (end exclusive). Same-day when `ini < end`; overnight when `ini > end`
     * (weekday is the start day).
     */
    IsActiveAt(at: Date): boolean {
        if (!this.IsValid || this.status !== 'AC'){
            return false;
        }

        const _weekdays = this.weekdays;
        if (!_weekdays || _weekdays.length < 7){
            return false;
        }

        const _ini = this._MinutesFromMidnight(this.ini);
        const _end = this._MinutesFromMidnight(this.end);
        if (_ini === null || _end === null || _ini === _end){
            return false;
        }

        const _atMinutes = at.getHours() * 60 + at.getMinutes();
        const _jsDay = at.getDay();
        const _wd = _jsDay === 0 ? 6 : _jsDay - 1;

        if (_ini < _end){
            if (_weekdays.charAt(_wd) !== '1'){
                return false;
            }
            return (_atMinutes >= _ini) && (_atMinutes < _end);
        }

        if (_atMinutes >= _ini){
            return _weekdays.charAt(_wd) === '1';
        }

        if (_atMinutes < _end){
            const _prevWd = _wd === 0 ? 6 : _wd - 1;
            return _weekdays.charAt(_prevWd) === '1';
        }

        return false;
    }

    private _MinutesFromMidnight(hhmm: string | null): number | null {
        if (!hhmm){
            return null;
        }

        const _parts = hhmm.split(':');
        if (_parts.length < 2){
            return null;
        }

        const _hh = parseInt(_parts[0], 10);
        const _mm = parseInt(_parts[1], 10);
        if (isNaN(_hh) || isNaN(_mm)){
            return null;
        }

        return (_hh * 60) + _mm;
    }
}
